"use client";
import React, { useMemo } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "@/navigation";
import { containerVariants, itemVariants } from "@/lib/utils/util";
import { motion } from "framer-motion";
import CustomImage from "../Image";

function Index({ categories }: { categories }) {
	const router = useRouter();
	
	const sortedCategories = useMemo(() => {
		if (!categories?.edges) return [];
		
		// 使用与 TopNavOne 相同的排序逻辑
		const SORT_ORDER = [
			"hydraulic-motor",
			"hydraulic-pump",
			"hydraulic-valve",
			"hydraulic-cylinder",
			"hydraulic-cooler",
			"hydraulic-system",
			"hydraulic-accessories",
			"hydraulic-fitting",
			"hydraulic-hose",
			"hydraulic-steering-control-unit"
		];
		
		return [...categories.edges]
			// .filter(item => item.node.slug !== "hydraulic-hose" && item.node.slug !== "hydraulic-system")
			.sort((a, b) => {
				const slugA = (a.node.slug || "").toLowerCase();
				const slugB = (b.node.slug || "").toLowerCase();

				let indexA = SORT_ORDER.indexOf(slugA);
				let indexB = SORT_ORDER.indexOf(slugB);

				// 如果没有精确匹配，尝试前缀匹配
				if (indexA === -1) indexA = SORT_ORDER.findIndex(key => slugA.includes(key));
				if (indexB === -1) indexB = SORT_ORDER.findIndex(key => slugB.includes(key));

				// 未在列表中的排到最后
				if (indexA === -1) indexA = 999;
				if (indexB === -1) indexB = 999;

				return indexA - indexB;
			})
			.slice(0, 6); // 只显示前6个，保持首页为6宫格
	}, [categories]);
	
	const t = useTranslations("plsyy");

	const textVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
	};

	return (
		<div className="w-full h-full flex justify-center pt-6 sm:pt-8 md:pt-12 lg:pt-16 xl:pt-[70px] overflow-hidden">
			<div className="3xl:w-[80vw] 3xl:max-w-none container mx-auto h-full flex flex-col">
				{/* 标题部分 - flex-shrink-0 确保不收缩 */}
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: false, amount: 0.3 }}
					variants={{
						hidden: { opacity: 0, y: 30 },
						visible: { opacity: 1, y: 0, transition: { duration: 0.8, staggerChildren: 0.2 } }
					}}
					className="my-5 md:my-10 2xl:my-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 md:gap-6 flex-shrink-0"
				>
					<div className="flex-1">
						<motion.h2 variants={textVariants} className="text-3xl md:text-5xl 2xl:text-5xl font-bold mb-2 sm:mb-3 md:mb-4 ">
							<span className="text-black max-md:text-2xl">{t("Product")}</span>{" "}
							<span className="text-mainColor max-md:text-2xl">{t("Series")}</span>
						</motion.h2>
						<motion.p variants={textVariants} className="text-xs sm:text-sm md:text-base 2xl:text-lg text-gray-600 max-w-4xl">
							{t("Product-Series-Description")}
						</motion.p>
					</div>
					<motion.div variants={textVariants}>
						<button
							onClick={() => router.push("/products")}
							className="group relative px-3 py-1.5 sm:px-4 sm:py-1.5 md:px-5 md:py-2 lg:px-6 lg:py-2.5 justify-center text-sm sm:text-base 2xl:text-lg border border-mainColor text-mainColor rounded-md overflow-hidden transition-colors duration-300 whitespace-nowrap"
						>
							<span className="relative z-10 group-hover:text-white transition-colors duration-300">{t("Learn-More")}</span>
							<span className="absolute inset-0 bg-mainColor transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
						</button>
					</motion.div>
				</motion.div>

				{/* 产品网格容器 - 6宫格展示并在移动端保持紧凑 */}
				<div className="flex-1 flex items-center justify-center pb-8 sm:pb-12 md:pb-16 lg:pb-20">
					<motion.div
						initial="hidden"
						whileInView="visible"
						viewport={{ once: false, amount: 0.2 }}
						variants={containerVariants}
						className="grid w-full grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-5 sm:gap-x-5 sm:gap-y-7 md:gap-x-8 md:gap-y-10 lg:gap-x-10 lg:gap-y-12 xl:gap-x-14 xl:gap-y-14"
					>
						{sortedCategories.map((item, index) => {
						let media = (item?.node?.media) ? JSON.parse(item?.node?.media) : [];
						const imageUrl = media[0]?.url || "/image/default-image2.webp";
						const categoryName = item.node?.translation?.name || item.node.name;

						return (
							<motion.div
								key={item.node.id}
								variants={itemVariants}
								className="group cursor-pointer relative"
								onClick={() => router.push(`/products/${item.node.slug}`)}
							>
								<div className="relative w-full aspect-[4/3] overflow-hidden">
									<style dangerouslySetInnerHTML={{__html: `
										.flip-image-container {
											perspective: 1000px;
										}
										.flip-inner {
											position: relative;
											width: 100%;
											height: 100%;
											transform-style: preserve-3d;
											transition: transform 0.6s ease-in-out;
										}
										.group:hover .flip-inner {
											transform: rotateY(180deg);
										}
										.flip-front,
										.flip-back {
											position: absolute;
											width: 100%;
											height: 100%;
											backface-visibility: hidden;
											-webkit-backface-visibility: hidden;
										}
										.flip-front {
											transform: rotateY(0deg);
										}
										.flip-back {
											transform: rotateY(180deg);
										}
									`}} />
									<div className="flip-image-container w-full h-full">
										<div className="flip-inner">
											<div className="flip-front">
												<CustomImage
													alt={categoryName}
													className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
													src={imageUrl}
													width={500}
													height={500}
												/>
											</div>
											<div className="flip-back">
												<CustomImage
													alt={categoryName}
													className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
													src={imageUrl}
													width={500}
													height={500}
												/>
											</div>
										</div>
									</div>
									{/* 产品名称 - 覆盖在图片底部 */}
									<div className="absolute bottom-0 left-0 w-full text-center px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-3 z-20">
										<h3 className="text-xs sm:text-sm md:text-base xl:text-lg 2xl:text-xl font-medium text-black group-hover:text-mainColor transition-colors relative z-10 drop-shadow-lg">
											{categoryName}
										</h3>
									</div>
								</div>
								{/* 蓝色渐变遮罩 - hover时从下往上铺满整个卡片到80% */}
								<div 
									className="absolute bottom-0 left-0 w-full h-0 group-hover:h-[80%] transition-all duration-500 ease-in-out pointer-events-none z-10"
									style={{
										background: 'linear-gradient(to top, rgba(31, 82, 142, 0.6) 0%, rgba(102, 162, 255, 0.4) 50%, rgba(230, 240, 255, 0.1) 80%, transparent 100%)'
									}}
								></div>
							</motion.div>
						);
					})}
					</motion.div>
				</div>
			</div>
		</div>
	);
}

export default Index;